import React from 'react';
import {
	Flex,
	Text,
	Spacer,
	Button,
	Table,
	Tbody,
	Tr,
	Td,
	Box,
	IconButton,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import Locale from '../../utils/translations';
import useTranslation from '../hooks/useTranslations';
import { LikeButton, DislikeButton } from '../icons/icons';
import { BonfireAPI, ApiType } from '../../models/BonfireApi';
import TranslationModel from '../../models/translations/translations';
import moment from 'moment';

const LanguageDetectionListItem = ({
	translation,
	onLikeButtonClicked,
	onDislikeButtonClicked,
}: {
	translation: TranslationModel;
	onLikeButtonClicked: (action: string, id: string) => void;
	onDislikeButtonClicked: (action: string, id: string) => void;
}) => {
	const getAudioFileUrl = () => {
		const env = translation.getInputAttribute('env');
		let envUrl = '';
		if (!env) {
			envUrl = 'staging.';
		} else {
			envUrl = env.includes('prod') ? '' : 'staging.';
		}
		return `https://api-qa.${envUrl}ai-core.boostlingo.com/files/getFile?type=language-detection&id=${translation.input.text}&env=${env}`;
	};

	const confidence = translation.output.attributes.find((attr: any) => attr.key == 'confidence');
	const transcript = translation.output.attributes.find((attr: any) => attr.key == 'transcript');

	return (
		<Tr bg="white" borderRadius="4px" fontWeight="600" fontSize="14px">
			<Td
				border="1px solid #F5F1F1"
				borderRight="none"
				p="8px 16px"
				borderStartRadius="4px"
			/>

			<Td borderTop="1px solid #F5F1F1" display="grid" gridTemplateColumns="auto 1fr">
				<Text variant="beTextDescription">Audio:</Text>
				<Text variant="beTextDescription" ml={'20px'}>
					{translation.input.text}
					<audio controls>
						<source src={getAudioFileUrl()} type="audio/wav" />
						Your browser does not support the audio element.
					</audio>
				</Text>
				{!!translation.provider && (
					<>
						<Text variant="beTextDescription" color="#1462F0">
							Provider:
						</Text>
						<Text variant="beTextDescription" color="#1462F0" ml={'20px'}>
							{translation.provider}
						</Text>
					</>
				)}
				<Text variant="beTextDescription" color="#1462F0">
					Recognized language:
				</Text>
				<Text variant="beTextDescription" color="#1462F0" ml={'20px'}>
					{translation.output.text}
				</Text>
				<Text variant="beTextDescription" color="#1462F0">
					Time used:
				</Text>
				<Text variant="beTextDescription" color="#1462F0" ml={'20px'}>
					{translation.getInputAttribute('time')}ms
				</Text>
				{!!transcript && (
					<>
						<Text variant="beTextDescription" color="#1462F0">
							Transcript:
						</Text>
						<Text variant="beTextDescription" color="#1462F0" ml={'20px'}>
							{transcript.value}
						</Text>
					</>
				)}
			</Td>
			<Td borderTop="1px solid #F5F1F1" borderBottom="1px solid #F5F1F1">
				<Box>
					<IconButton
						onClick={() => onLikeButtonClicked('yes', translation._id)}
						colorScheme="#fff"
						size={'lg'}
						aria-label="yes-answer"
						icon={
							<LikeButton fill={translation.answer === 'yes' ? 'green' : '#1462F0'} />
						}
					/>
					<IconButton
						onClick={() => onDislikeButtonClicked('no', translation._id)}
						colorScheme="#fff"
						size={'lg'}
						aria-label="yes-answer"
						icon={
							<DislikeButton fill={translation.answer === 'no' ? 'red' : '#1462F0'} />
						}
					/>
				</Box>
				{!!confidence && <Text>Confidence: {parseFloat(confidence.value).toFixed(3)}</Text>}
			</Td>
		</Tr>
	);
};
const LanguageDetection: React.FC = () => {
	const [searchParams] = useSearchParams();

	const from = searchParams.get('from') as string;
	const to = searchParams.get('to') as string;
	const lang = searchParams.get('lang') as string;

	const {
		translations,
		translationsLoading,
		moveNext,
		movePrev,
		offset,
		pageSize,
		mutateTranslations,
	} = useTranslation(
		'language_detection',
		undefined,
		undefined,
		lang,
		from,
		to ? moment(to).add(1, 'day').toISOString() : to,
	);

	const onLikeButtonClicked = async (action: string, id: string) => {
		const api = new BonfireAPI(ApiType.QaApi);
		await api.patch('/qa/updateAnswer', { action, id }).then(() => {
			mutateTranslations();
		});
	};

	if (translationsLoading) {
		return <div>loading ...</div>;
	}

	const noNavigation = translations && translations?.length < pageSize && offset == 0;

	return (
		<Box display={'flex'} flexWrap={'wrap'} justifyContent={'center'}>
			<Flex direction="column" alignItems="center" justifyContent="space-between" w={'60%'}>
				<Flex direction="row" w="full">
					<Text fontSize="18px" fontWeight="700">
						{Locale.get('Language detection')}
					</Text>
					<Spacer />
					{!noNavigation && (
						<>
							<Button
								isDisabled={offset === 0}
								variant="beSecondaryButton"
								size="sm"
								mr={5}
								onClick={movePrev}
							>
								previous
							</Button>
							<Text variant="beTextDescription">
								{offset + 1} .. {offset + pageSize}
							</Text>
							<Button
								isDisabled={translations?.length !== 20}
								variant="bePrimaryButton"
								size="sm"
								ml={5}
								onClick={moveNext}
							>
								next
							</Button>
						</>
					)}
				</Flex>
				<Flex direction="row" w="full">
					{!!lang && <Text ml="20px">language: {lang}</Text>}
					{!!from && <Text ml="20px">from: {moment(from).format('YYYY-MM-DD')}</Text>}
					{!!to && <Text ml="20px">to: {moment(to).format('YYYY-MM-DD')}</Text>}
				</Flex>
			</Flex>
			<Table
				mt="23px"
				variant="unstyled"
				style={{ borderCollapse: 'separate', borderSpacing: '0 0.5em' }}
				w={'60%'}
			>
				<Tbody>
					{translations?.map((translation: TranslationModel) => (
						<LanguageDetectionListItem
							translation={translation}
							key={translation._id}
							onLikeButtonClicked={() => onLikeButtonClicked('yes', translation._id)}
							onDislikeButtonClicked={() =>
								onLikeButtonClicked('no', translation._id)
							}
						/>
					))}
				</Tbody>
			</Table>
		</Box>
	);
};

export default LanguageDetection;
